import type { AccountContribution, FinancePeriod, Statement } from '../components/finance-statement-model';
import type { CatalogTransport } from './warehouse-catalog-client';

export type Currency = 'USD' | 'GBP';
export type LiveStatement = {
  statement: Statement; periods: FinancePeriod[];
  liveSource: { label: string; fetchedAt: number; note: string };
};
export type StatementResult =
  | { state: 'loading' | 'unauthenticated' | 'denied' | 'error' }
  | { state: 'ready'; data: LiveStatement };
export type IncomeTransaction = {
  id: string; number: string; type: string; date: string; customerVendor: string; memo: string;
  departmentId: string; currency: Currency; amount: number;
};

/** Compare the complete selected transaction detail to its reported account line. */
export function compareIncomeTransactions(rows: IncomeTransaction[], accountAmount: number) {
  const total = rows.reduce((sum, row) => sum + row.amount, 0);
  if (!Number.isFinite(total) || !Number.isFinite(accountAmount)) return null;
  // NetSuite amounts are currency values. Compare in integer cents so binary
  // floating point noise (for example 0.10 + 0.20) cannot create false breaks.
  const totalCents = Math.round(total * 100);
  const accountCents = Math.round(accountAmount * 100);
  if (!Number.isSafeInteger(totalCents) || !Number.isSafeInteger(accountCents)) return null;
  const normalizedTotal = totalCents / 100;
  const difference = (totalCents - accountCents) / 100;
  return Number.isSafeInteger(totalCents - accountCents)
    ? { total: normalizedTotal, difference, ties: difference === 0 } : null;
}

export function incomeTransactionsCsv(rows: IncomeTransaction[]): string {
  const fields = ['Date', 'Type', 'Number', 'Customer / vendor', 'Memo', 'Department ID', 'Amount', 'Currency'];
  const cell = (value: string | number) => {
    const raw = typeof value === 'string' && /^[\s\u0000-\u001f]*[=+\-@]/.test(value) ? `'${value}` : String(value);
    return `"${raw.replace(/"/g, '""')}"`;
  };
  return [fields, ...rows.map((row) => [row.date, row.type, row.number, row.customerVendor,
    row.memo, row.departmentId, row.amount, row.currency])].map((line) => line.map(cell).join(',')).join('\r\n');
}

export function parseIncomeTransactionResponse(value: unknown, account: string, periodId: string, currency: Currency): IncomeTransaction[] {
  if (!record(value) || value.source !== 'netsuite' || value.view !== 'income_statement_transactions_v1'
    || value.account !== account || value.period_id !== periodId || value.currency !== currency
    || value.truncated !== false || value.limit_applied !== false || !Array.isArray(value.rows)
    || value.rows.length > 20000) throw new Error('Unusable transaction detail');
  return value.rows.map((row) => {
    if (!record(row) || !text(row.transaction_id) || !text(row.transaction_number)
      || !text(row.transaction_type) || !text(row.transaction_date) || typeof row.customer_vendor !== 'string'
      || row.customer_vendor.length > 500 || typeof row.memo !== 'string' || row.memo.length > 2000
      || typeof row.department_id !== 'string' || row.department_id.length > 30
      || row.currency !== currency || typeof row.amount !== 'number' || !Number.isFinite(row.amount)) {
      throw new Error('Invalid transaction row');
    }
    return { id: row.transaction_id, number: row.transaction_number, type: row.transaction_type,
      date: row.transaction_date, customerVendor: row.customer_vendor, memo: row.memo,
      departmentId: row.department_id, currency, amount: row.amount };
  });
}

const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0 && v.length <= 200;
const lines = [
  ['revenue', 'Revenue'], ['cogs', 'Cost of goods sold'], ['gross_profit', 'Gross profit'],
  ['opex', 'Operating expenses'], ['operating_income', 'Operating income'],
  ['other', 'Other income / expense'], ['net_income', 'Net income'],
  ['addback_total', 'EBITDA add-backs'], ['ebitda', 'EBITDA'],
] as const;
const sectionByType: Readonly<Record<string, string>> = {
  Income: 'revenue', OthIncome: 'revenue', COGS: 'cogs', Expense: 'opex', OthExpense: 'other',
};
function accountContributions(column: Record<string, unknown>, rowId: string, addbacks: readonly string[]): AccountContribution[] {
  const source = column.lines as { gl_code: string; account_name: string; account_type: string; amount: number }[];
  const sections = rowId === 'revenue' ? ['revenue']
    : rowId === 'cogs' ? ['cogs']
      : rowId === 'gross_profit' ? ['revenue', 'cogs']
        : rowId === 'opex' ? ['opex']
          : rowId === 'operating_income' ? ['revenue', 'cogs', 'opex']
            : rowId === 'other' ? ['other'] : ['revenue', 'cogs', 'opex', 'other'];
  const contributions: AccountContribution[] = [];
  if (rowId !== 'addback_total') {
    for (const line of source) if (sections.includes(sectionByType[line.account_type])) {
      contributions.push({ id: JSON.stringify([line.gl_code, line.account_name, line.account_type, false]),
        glCode: line.gl_code, accountName: line.account_name, accountType: line.account_type, amount: line.amount });
    }
  }
  if (rowId === 'addback_total' || rowId === 'ebitda') {
    for (const line of source) if (addbacks.includes(line.gl_code)) {
      contributions.push({ id: JSON.stringify([line.gl_code, line.account_name, line.account_type, true]),
        glCode: line.gl_code, accountName: line.account_name, accountType: line.account_type,
        amount: -line.amount, adjustment: true });
    }
  }
  return contributions;
}

/** Exact current API contract; neither response counts nor HTTP 200 prove completeness. */
export function parseIncomeStatement(value: unknown, currency: Currency, departmentUnavailable = false): LiveStatement {
  if (!record(value) || value.source !== 'netsuite' || value.view !== 'income_statement_v1'
    || value.currency !== currency || value.truncated !== false || value.limit_applied !== false
    || value.eliminations !== 'excluded' || !Number.isSafeInteger(value.fetchedAt)
    || (value.fetchedAt as number) <= 0 || (value.fetchedAt as number) > 253402300799
    || !Array.isArray(value.periods) || value.periods.length === 0 || value.periods.length > 21
    || !Array.isArray(value.columns) || value.columns.length !== value.periods.length
    || !text(value.addback_note) || !Array.isArray(value.addback_accounts) || value.addback_accounts.length > 100
    || !value.addback_accounts.every((account) => typeof account === 'string' && /^[0-9]{1,20}$/.test(account))) throw new Error('Unusable income statement');
  const addbackAccounts = value.addback_accounts as string[];
  const seen = new Set<string>();
  const columns = value.columns.map((column, index) => {
    if (!record(column) || !text(column.period) || !text(column.period_id)
      || !/^[1-9][0-9]{0,19}$/.test(column.period_id)
      || column.period !== (value.periods as unknown[])[index]
      || seen.has(column.period) || (!record(column.totals) && !(departmentUnavailable && column.availability === 'sb1_unavailable' && column.totals === null))) throw new Error('Invalid period');
    seen.add(column.period);
    for (const key of ['is_open', 'closed']) {
      if (column[key] !== undefined && typeof column[key] !== 'boolean') throw new Error('Invalid close status');
    }
    if (typeof column.is_open === 'boolean' && typeof column.closed === 'boolean'
      && column.is_open === column.closed) throw new Error('Conflicting close status');
    const unavailable = departmentUnavailable && column.availability === 'sb1_unavailable';
    if (unavailable && (!Array.isArray(column.unavailable_accounts) || !column.unavailable_accounts.length || column.totals !== null
      || !column.unavailable_accounts.every(a => record(a) && text(a.gl_code) && text(a.account_name)))) throw new Error('Missing unavailable account disclosure');
    if (!Array.isArray(column.lines) || column.lines.length > 5000 || !column.lines.every((line) => record(line)
      && typeof line.gl_code === 'string' && line.gl_code.length <= 50 && text(line.account_name)
      && typeof line.account_type === 'string' && Object.hasOwn(sectionByType, line.account_type)
      && typeof line.amount === 'number' && Number.isFinite(line.amount))) {
      throw new Error('Invalid account detail');
    }
    for (const [key] of unavailable ? [] : lines) {
      if (typeof (column.totals as Record<string, unknown>)[key] !== 'number' || !Number.isFinite((column.totals as Record<string, unknown>)[key])) throw new Error('Missing total');
    }
    return { period: column.period, periodId: column.period_id, totals: column.totals as Record<string, unknown>, lines: column.lines, unavailable,
      unavailableNote: unavailable ? `SB1 source amount missing for ${(column.unavailable_accounts as { gl_code: string; account_name: string }[]).map(a => `${a.gl_code} · ${a.account_name}`).join(', ')}. All totals for this period are unavailable; no zero inferred.` : '',
      status: (typeof column.is_open === 'boolean' ? (column.is_open ? 'open' : 'closed')
        : typeof column.closed === 'boolean' ? (column.closed ? 'closed' : 'open') : 'unknown') as FinancePeriod['status'] };
  });
  return {
    // Dates are not exposed by this API: do not derive them from English labels.
    periods: columns.map(({ period, periodId, status }) => ({ id: period, sourceId: periodId, label: period, endDate: '', status })),
    statement: {
      id: 'income-statement', title: 'Income statement · summary', currency, kind: 'flow',
      entity: 'All returned subsidiaries in this base currency; eliminations excluded',
      basis: 'Posting periods · unconverted subsidiary base amounts, not FX consolidation',
      rows: lines.map(([id, label]) => ({ id, label, emphasis: ['gross_profit', 'operating_income', 'net_income', 'ebitda'].includes(id),
        accounts: Object.fromEntries(columns.map((column) => [column.period, column.unavailable ? []
          : accountContributions(column as unknown as Record<string, unknown>, id, addbackAccounts)])),
        values: Object.fromEntries(columns.map((column) => [column.period, column.unavailable
          ? { status: 'unavailable' as const, amount: null, note: column.unavailableNote }
          : { status: 'measured' as const, amount: column.totals[id] as number,
            note: `NetSuite income_statement_v1 / ${column.period} / ${id}. API-reported amount, not independently reconciled.${id === 'ebitda' || id === 'addback_total' ? ` ${value.addback_note}` : ''}`,
          }])),
      })),
    },
    liveSource: {
      label: 'NetSuite via Finance API — not a warehouse database read', fetchedAt: value.fetchedAt as number,
      note: 'API-backed summary. Environment/account must be confirmed for UAT; the API does not return their identity. Measured means received from the API, not tied out. No budget, forecast or missing amount is substituted.',
    },
  };
}

/** Department response must match the precise requested scope before any totals mount. */
export function parseDepartmentIncomeStatement(value: unknown, currency: Currency, department: string): LiveStatement {
  if (!/^[1-9][0-9]{0,14}$/.test(department) || !record(value)
    || value.view !== 'department_income_statement_v1' || !record(value.scope)
    || value.scope.kind !== 'department' || !Array.isArray(value.scope.department_ids)
    || value.scope.department_ids.length !== 1 || value.scope.department_ids[0] !== department) {
    throw new Error('Invalid department statement scope');
  }
  const data = parseIncomeStatement({ ...value, view: 'income_statement_v1' }, currency, true);
  data.statement.id = 'department-income-statement';
  data.statement.title = `Department P&L · department ${department} · summary`;
  data.statement.entity = `NetSuite department ${department} · subsidiary base currency ${currency}; eliminations excluded`;
  data.liveSource.note += ' Server filters this department before aggregation and verifies every fetched row. This is not the entity-wide income statement.';
  for (const row of data.statement.rows) for (const value of Object.values(row.values)) {
    if ('note' in value && typeof value.note === 'string') value.note = value.note.replace('income_statement_v1', `department_income_statement_v1 / department ${department}`);
  }
  return data;
}

/** Fixed endpoints only, with abort and generation guards shared by both summaries. */
function createStatementLoader(transport: CatalogTransport, publish: (state: StatementResult) => void, scoped: boolean) {
  let generation = 0;
  let controller: AbortController | undefined;
  let disposed = false;
  return {
    async refresh(currency: Currency, department?: string) {
      if (disposed) return;
      const request = ++generation;
      controller?.abort();
      controller = new AbortController();
      publish({ state: 'loading' });
      let result: StatementResult;
      try {
        if (currency !== 'USD' && currency !== 'GBP') throw new Error('Unsupported currency');
        if (scoped && (!department || !/^[1-9][0-9]{0,14}$/.test(department))) throw new Error('Invalid department');
        const endpoint = scoped ? 'department-income-statement' : 'income-statement';
        const path = `/api/finance/${endpoint}?months=21&currency=${currency}&eliminations=exclude${scoped ? `&department=${department}` : ''}`;
        const response = await transport(path, {
          method: 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (response.status === 401) result = { state: 'unauthenticated' };
        else if (response.status === 403) result = { state: 'denied' };
        else if (!response.ok || response.redirected || !/^application\/json(?:;|$)/i.test(response.headers.get('Content-Type') ?? '')) {
          result = { state: 'error' };
        } else result = { state: 'ready', data: scoped
          ? parseDepartmentIncomeStatement(await response.json(), currency, department!)
          : parseIncomeStatement(await response.json(), currency) };
      } catch { result = { state: 'error' }; }
      if (!disposed && request === generation) publish(result);
    },
    dispose() { disposed = true; generation++; controller?.abort(); },
  };
}
export const createIncomeStatementLoader = (transport: CatalogTransport, publish: (state: StatementResult) => void) => createStatementLoader(transport, publish, false);
export const createDepartmentIncomeLoader = (transport: CatalogTransport, publish: (state: StatementResult) => void) => createStatementLoader(transport, publish, true);
